import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';
import CardContainer from '../../../components/card/card-container.jsx';
import Api from '../../../api/global.service';
import Swal from 'sweetalert2';
import QuestionSelectorTree from './components/QuestionSelectorTree.jsx';
import MielQuestionSelectorTree from './components/miel/MielQuestionSelectorTree.jsx';

const ReportesCreate = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { id_ciclo, id_kobo_ficha_principal } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [fichaId, setFichaId] = useState(id_kobo_ficha_principal || null);
  // Miel: el árbol y la selección se basan en el name (clave del JSON de Mongo)
  const [esMiel, setEsMiel] = useState(false);
  
  // Data sources
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [producers, setProducers] = useState([]);
  
  // Form state
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProducers, setSelectedProducers] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let currentReport = null;
        let resolvedFichaId = fichaId;

        if (isEditMode) {
          const reportRes = await Api.get('kobo-ficha-descargas', id);
          currentReport = reportRes.data;
          setReportName(currentReport?.nombre || '');
          setReportDescription(currentReport?.descripcion || '');
          resolvedFichaId = currentReport?.id_kobo_ficha_principal || null;
          setFichaId(resolvedFichaId);
        } else {
          setReportName('');
          setReportDescription('');
        }

        const params = resolvedFichaId ? { id_kobo_ficha_principal: resolvedFichaId } : {};

        const mielRes = resolvedFichaId ? await Api.getAll('descargas-masivas/miel/preguntas', params) : null;
        if (mielRes?.data?.es_miel) {
          setEsMiel(true);
          setQuestions(mielRes.data.preguntas || []);

          const producersRes = await Api.getAll('descargas-masivas/productores');
          const prods = (producersRes.data || []).map(p => ({
            value: p.id,
            label: p.nombre_completo || p.nombre
          }));
          setProducers(prods);

          setSelectedQuestions(new Set(currentReport?.config?.preguntas || []));

          const producerIds = new Set(currentReport?.config?.productores || []);
          setSelectedProducers(prods.filter(p => producerIds.has(p.value)));

          setLoading(false);
          return;
        }

        const [questionsRes, categoriesRes, producersRes] = await Promise.all([
          Api.getAll('descargas-masivas/preguntas', params),
          Api.getAll('descargas-masivas/categorias', params),
          Api.getAll('descargas-masivas/productores')
        ]);

        const rawQuestions = questionsRes.data || [];
        const questionsWithId = rawQuestions.map(q => ({
          ...q,
          id: q.id || q.id_kobo_ficha_principal_pregunta
        }));
        setQuestions(questionsWithId);

        const cats = (categoriesRes.data || []).map(c => ({
          value: c.id_kobo_ficha_principal_categoria || c.id,
          label: c.nombre,
          obligatorio: c.obligatorio,
        }));
        setCategories(cats);

        const mandatoryCats = cats.filter(c => c.obligatorio == 1 || c.obligatorio === true);
        setSelectedCategories(mandatoryCats);

        const prods = (producersRes.data || []).map(p => ({
          value: p.id,
          label: p.nombre_completo || p.nombre
        }));
        setProducers(prods);

        if (currentReport?.config?.preguntas && Array.isArray(currentReport.config.preguntas) && currentReport.config.preguntas.length > 0) {
          const selectedConfigIds = new Set(currentReport.config.preguntas);
          const initialSelectedQuestions = new Set();
          questionsWithId.forEach(q => {
            if (selectedConfigIds.has(q.config_id_pregunta)) {
              initialSelectedQuestions.add(q.id);
            }
          });
          setSelectedQuestions(initialSelectedQuestions);
        } else {
          const initialSelectedQuestions = new Set();
          mandatoryCats.forEach(cat => {
            const catQuestions = questionsWithId.filter(q => q.categorias && q.categorias.some(c => c.id_kobo_ficha_principal_categoria === cat.value));
            catQuestions.forEach(q => initialSelectedQuestions.add(q.id));
          });
          setSelectedQuestions(initialSelectedQuestions);
        }

        if (currentReport?.config?.productores && Array.isArray(currentReport.config.productores)) {
          const producerIds = new Set(currentReport.config.productores);
          setSelectedProducers(prods.filter(p => producerIds.has(p.value)));
        } else {
          setSelectedProducers([]);
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'No se pudieron cargar los datos iniciales', 'error');
        setLoading(false);
      }
    };

    load();
  }, [id, isEditMode]);

  const handleCategoryCheckboxChange = (category, checked) => {
    let newSelectedCategories;
    if (checked) {
        newSelectedCategories = [...selectedCategories, category];
    } else {
        newSelectedCategories = selectedCategories.filter(c => c.value !== category.value);
    }
    
    // Calculate questions based on new category selection
    const currentSelectedIds = new Set(selectedCategories.map(c => c.value));
    const newSelectedIds = new Set(newSelectedCategories.map(c => c.value));
    
    // Re-calculate selected questions from scratch or diff
    // Diff approach:
    const newSelectedQuestions = new Set(selectedQuestions);
    
    if (checked) {
        // Add questions for this category
        const catQuestions = questions.filter(q => q.categorias && q.categorias.some(c => c.id_kobo_ficha_principal_categoria === category.value));
        catQuestions.forEach(q => newSelectedQuestions.add(q.id));
    } else {
        // Remove questions for this category ONLY IF they are not covered by other selected categories
        // But wait, user might have manually selected/deselected questions.
        // The original logic was: "Add questions from added categories" and "Remove questions from removed categories".
        // If I uncheck a category, I should probably remove its questions.
        
        const catQuestions = questions.filter(q => q.categorias && q.categorias.some(c => c.id_kobo_ficha_principal_categoria === category.value));
        catQuestions.forEach(q => {
             // Check if this question belongs to another selected category
             // If so, do we keep it? 
             // The simple logic: unchecking category removes its questions from selection.
             newSelectedQuestions.delete(q.id);
        });
        
        // However, if a question belongs to multiple categories and one is still checked, maybe we should keep it?
        // Let's stick to the previous logic: explicitly remove questions of the removed category.
        // If the user wants it back, they can re-select the question or the other category.
    }
    
    setSelectedCategories(newSelectedCategories);
    setSelectedQuestions(newSelectedQuestions);
  };

  const handleQuestionToggle = (id) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedQuestions(newSelected);
  };

  const handleProducerChange = (selectedOptions) => {
    setSelectedProducers(selectedOptions);
  };

  const getPayload = () => {
    if (esMiel) {
      const payload = {
        nombre: reportName,
        descripcion: reportDescription,
        config: {
          fuente: 'mongo',
          preguntas: Array.from(selectedQuestions),
          productores: selectedProducers.map(p => p.value),
        }
      };
      if (!isEditMode) {
        payload.id_ciclo = id_ciclo;
        payload.id_kobo_ficha_principal = id_kobo_ficha_principal;
      }
      return payload;
    }

    const selectedConfigIds = Array.from(selectedQuestions).map(id => {
        const q = questions.find(q => q.id === id);
        return q ? q.config_id_pregunta : null;
    }).filter(id => id != null);

    const payload = {
      nombre: reportName,
      descripcion: reportDescription,
      config: {
        preguntas: selectedConfigIds,
        productores: selectedProducers.map(p => p.value), // If empty, backend should treat as "all"
      }
    };

    if (!isEditMode) {
      payload.id_ciclo = id_ciclo;
      payload.id_kobo_ficha_principal = id_kobo_ficha_principal;
    }

    return payload;
  };

  const handleSubmit = () => {
    if (!isEditMode && !id_kobo_ficha_principal) {
      Swal.fire('Error', 'No se ha identificado la ficha principal. Por favor regrese al listado e intente nuevamente.', 'error');
      return;
    }
    if (!reportName.trim()) {
      Swal.fire('Error', 'El nombre del reporte es obligatorio', 'error');
      return;
    }
    if (selectedQuestions.size === 0) {
      Swal.fire('Error', 'Debe seleccionar al menos una pregunta', 'error');
      return;
    }

    setLoading(true);
    const payload = getPayload();

    const request = isEditMode
      ? Api.update('kobo-ficha-descargas', id, payload)
      : Api.create('kobo-ficha-descargas', payload);

    request
      .then(() => {
        setLoading(false);
        const msg = isEditMode
          ? 'Se guardaron los cambios y la regeneración del reporte ha iniciado.'
          : 'La generación del reporte ha iniciado. Podrá ver el progreso en el listado.';

        Swal.fire('Éxito', msg, 'success')
          .then(() => history.push('/reportes/personalizados/list'));
      })
      .catch(err => {
        setLoading(false);
        console.error(err);
        Swal.fire('Error', isEditMode ? 'Error al actualizar el reporte' : 'Error al generar el reporte', 'error');
      });
  };

  return (
    <CardContainer title={isEditMode ? 'Editar Reporte' : 'Nuevo Reporte'}>
      <div className="form-group">
        <label>Nombre del reporte *</label>
        <input 
          type="text" 
          className="form-control" 
          value={reportName} 
          onChange={e => setReportName(e.target.value)} 
        />
      </div>
      
      <div className="form-group">
        <label>Descripción</label>
        <textarea 
          className="form-control" 
          value={reportDescription} 
          onChange={e => setReportDescription(e.target.value)} 
        />
      </div>

      <div className="row">
        <div className="col-md-12">
            <div className="form-group">
                <label>Filtrar por Productores (Dejar vacío para todos)</label>
                <Select
                    isMulti
                    options={producers}
                    value={selectedProducers}
                    onChange={handleProducerChange}
                    placeholder="Seleccionar productores..."
                />
            </div>
        </div>
      </div>
      
      {/* 
      <div className="row">
        <div className="col-md-12">
            <div className="form-group">
                <label>Seleccionar preguntas por Categoría</label>
                <div className="border rounded p-3 bg-light" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {categories.length === 0 ? (
                        <div className="text-muted">No hay categorías disponibles</div>
                    ) : (
                        <div className="d-flex flex-wrap">
                            {categories.map(cat => (
                                <div key={cat.value} className="form-check mr-4 mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`cat-${cat.value}`}
                                        checked={selectedCategories.some(c => c.value === cat.value)}
                                        onChange={(e) => handleCategoryCheckboxChange(cat, e.target.checked)}
                                        disabled={cat.obligatorio == 1 || cat.obligatorio === true}
                                    />
                                    <label className="form-check-label" htmlFor={`cat-${cat.value}`} style={{ cursor: 'pointer' }}>
                                        {cat.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>
      */}

      <div className="form-group mt-4">
        <label><strong>Preguntas ({selectedQuestions.size} seleccionadas)</strong></label>
        {esMiel ? (
          <MielQuestionSelectorTree
            questions={questions}
            selectedQuestionIds={selectedQuestions}
            onSelectionChange={setSelectedQuestions}
          />
        ) : (
          <QuestionSelectorTree 
              questions={questions}
              selectedQuestionIds={selectedQuestions}
              onSelectionChange={setSelectedQuestions}
          />
        )}
      </div>

      <div className="mt-4 text-right">
        <button className="btn btn-secondary mr-2" onClick={() => history.push('/reportes/list')} disabled={loading}>
          Cancelar
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (isEditMode ? 'Guardando...' : 'Generando...') : (isEditMode ? 'Guardar y Regenerar' : 'Generar Reporte')}
        </button>
      </div>
    </CardContainer>
  );
};

export default ReportesCreate;
